import { useState, useCallback, useMemo, useEffect } from "react";
import type { NewsletterSubscriber } from "@/mocks/admin";
import AdminLayout from "@/components/feature/AdminLayout";
import MobileTableCards from "@/components/feature/MobileTableCards";
import { useNewsletterDB } from "@/hooks/useNewsletterDB";

/* ─── CSV Export ─── */
function exportSubscribersCSV(subscribers: NewsletterSubscriber[], filename: string) {
  const headers = ["ID", "E-Mail", "Name", "Status", "Quelle", "Anmeldedatum"];
  const rows = subscribers.map((s) => [
    s.id,
    s.email,
    s.name,
    s.isActive ? "Aktiv" : "Inaktiv",
    s.source,
    new Date(s.subscribedAt).toLocaleDateString("de-CH"),
  ]);
  const csv = [headers.join(";"), ...rows.map((r) => r.join(";"))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/* ─── Delete Modal ─── */
function DeleteModal({
  subscriber,
  onConfirm,
  onCancel,
}: {
  subscriber: NewsletterSubscriber | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!subscriber) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl p-5 md:p-6 max-w-sm w-full shadow-lg">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <i className="ri-delete-bin-line text-red-500 text-xl"></i>
        </div>
        <h3 className="text-lg font-bold text-hj-dark text-center font-heading">Abonnent löschen</h3>
        <p className="text-sm text-hj-gray text-center mt-2 font-body">
          Möchten Sie <strong className="text-hj-dark">{subscriber.email}</strong> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div className="flex gap-3 mt-5">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 border border-hj-gray-border rounded-md text-sm font-medium text-hj-dark hover:bg-hj-gray-light transition-colors cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Admin Newsletter ─── */
export default function AdminNewsletter() {
  const { subscribers: dbSubscribers, loading, usingMock, deleteSubscriber: dbDelete, unsubscribe: dbUnsubscribe } = useNewsletterDB();
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);

  useEffect(() => {
    setSubscribers(dbSubscribers);
  }, [dbSubscribers]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"" | "active" | "inactive">("");
  const [filterSource, setFilterSource] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "email" | "name">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<NewsletterSubscriber | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exportToast, setExportToast] = useState(false);

  const sources = useMemo(
    () => Array.from(new Set(subscribers.map((s) => s.source))),
    [subscribers]
  );

  const filtered = useMemo(() => {
    let result = subscribers.filter((s) => {
      const matchesSearch =
        s.email.toLowerCase().includes(search.toLowerCase()) ||
        s.name.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        !filterStatus || (filterStatus === "active" ? s.isActive : !s.isActive);
      const matchesSource = !filterSource || s.source === filterSource;
      return matchesSearch && matchesStatus && matchesSource;
    });

    result.sort((a, b) => {
      let cmp = 0;
      if (sortBy === "date") {
        cmp = new Date(a.subscribedAt).getTime() - new Date(b.subscribedAt).getTime();
      } else if (sortBy === "email") {
        cmp = a.email.localeCompare(b.email);
      } else {
        cmp = a.name.localeCompare(b.name);
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });

    return result;
  }, [subscribers, search, filterStatus, filterSource, sortBy, sortOrder]);

  const total = subscribers.length;
  const active = subscribers.filter((s) => s.isActive).length;
  const inactive = total - active;
  const activeFilterCount = [filterStatus, filterSource].filter(Boolean).length;

  const toggleStatus = useCallback(async (id: string) => {
    const sub = subscribers.find((s) => s.id === id);
    if (!sub) return;
    if (sub.isActive) {
      await dbUnsubscribe(id);
    } else {
      // reactivate — just update local state for now
      setSubscribers((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: true } : s)));
    }
    setSubscribers((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));
  }, [subscribers, dbUnsubscribe]);

  const deleteSubscriber = useCallback(async (id: string) => {
    await dbDelete(id);
    setSubscribers((prev) => prev.filter((s) => s.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, [dbDelete]);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTarget) {
      deleteSubscriber(deleteTarget.id);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteSubscriber]);

  const handleBulkDelete = useCallback(() => {
    setSubscribers((prev) => prev.filter((s) => !selectedIds.has(s.id)));
    setSelectedIds(new Set());
  }, [selectedIds]);

  const handleExport = useCallback(() => {
    const dataToExport = selectedIds.size > 0
      ? subscribers.filter((s) => selectedIds.has(s.id))
      : filtered;
    const timestamp = new Date().toISOString().slice(0, 10);
    exportSubscribersCSV(dataToExport, `helvetik-newsletter-${timestamp}.csv`);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 3000);
  }, [subscribers, filtered, selectedIds]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((s) => s.id)));
    }
  }, [filtered, selectedIds]);

  const clearFilters = useCallback(() => {
    setSearch("");
    setFilterStatus("");
    setFilterSource("");
    setSortBy("date");
    setSortOrder("desc");
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-hj-dark font-heading">Newsletter</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-hj-gray font-body">Abonnenten verwalten, exportieren und analysieren</p>
              {!loading && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${usingMock ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {usingMock ? "Mock-Daten" : "Live-Daten"}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm font-medium text-hj-dark hover:bg-hj-gray-light transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-download-line"></i>
              <span className="hidden sm:inline">CSV Export</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button
            onClick={() => setFilterStatus("")}
            className={`bg-white rounded-lg p-4 border text-center transition-colors cursor-pointer ${
              !filterStatus ? "border-hj-red ring-1 ring-hj-red/20" : "border-hj-gray-border hover:border-hj-gray"
            }`}
          >
            <p className="text-xl font-bold text-hj-dark">{total}</p>
            <p className="text-xs text-hj-gray font-body">Gesamt</p>
          </button>
          <button
            onClick={() => setFilterStatus(filterStatus === "active" ? "" : "active")}
            className={`bg-white rounded-lg p-4 border text-center transition-colors cursor-pointer ${
              filterStatus === "active" ? "border-emerald-500 ring-1 ring-emerald-500/20" : "border-hj-gray-border hover:border-emerald-300"
            }`}
          >
            <p className="text-xl font-bold text-emerald-600">{active}</p>
            <p className="text-xs text-hj-gray font-body">Aktiv</p>
          </button>
          <button
            onClick={() => setFilterStatus(filterStatus === "inactive" ? "" : "inactive")}
            className={`bg-white rounded-lg p-4 border text-center transition-colors cursor-pointer ${
              filterStatus === "inactive" ? "border-gray-400 ring-1 ring-gray-400/20" : "border-hj-gray-border hover:border-gray-300"
            }`}
          >
            <p className="text-xl font-bold text-gray-400">{inactive}</p>
            <p className="text-xs text-hj-gray font-body">Inaktiv</p>
          </button>
        </div>

        {/* Search + Filters */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="E-Mail oder Name suchen..."
                className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                activeFilterCount > 0
                  ? "border-hj-red text-hj-red bg-hj-red/5"
                  : "border-hj-gray-border text-hj-dark hover:bg-hj-gray-light"
              }`}
            >
              <i className="ri-filter-3-line"></i>
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-hj-red text-white rounded-full">{activeFilterCount}</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-3 pt-3 border-t border-hj-gray-border grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-hj-gray mb-1 block font-body">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as "" | "active" | "inactive")}
                  className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                >
                  <option value="">Alle Status</option>
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-hj-gray mb-1 block font-body">Quelle</label>
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                >
                  <option value="">Alle Quellen</option>
                  {sources.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-hj-gray mb-1 block font-body">Sortierung</label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as "date" | "email" | "name")}
                    className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                  >
                    <option value="date">Datum</option>
                    <option value="email">E-Mail</option>
                    <option value="name">Name</option>
                  </select>
                  <button
                    onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
                    className="px-3 py-2 border border-hj-gray-border rounded-md text-sm hover:bg-hj-gray-light transition-colors cursor-pointer"
                    aria-label={sortOrder === "asc" ? "Absteigend sortieren" : "Aufsteigend sortieren"}
                  >
                    <i className={sortOrder === "asc" ? "ri-sort-asc" : "ri-sort-desc"}></i>
                  </button>
                </div>
              </div>
              {(search || filterStatus || filterSource || sortBy !== "date" || sortOrder !== "desc") && (
                <div className="sm:col-span-3 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-hj-red hover:text-hj-red-light transition-colors font-body cursor-pointer"
                  >
                    Alle Filter zurücksetzen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div className="bg-hj-red/5 border border-hj-red/20 rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-sm text-hj-dark font-body">
              <strong>{selectedIds.size}</strong> Abonnenten ausgewählt
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="px-3 py-1.5 text-sm text-hj-red hover:bg-hj-red/10 rounded-md transition-colors cursor-pointer font-body"
              >
                Exportieren
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer font-body"
              >
                Löschen
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-sm text-hj-gray hover:bg-hj-gray-light rounded-md transition-colors cursor-pointer font-body"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden lg:block bg-white rounded-lg border border-hj-gray-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-hj-gray-light">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selectedIds.size === filtered.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-hj-gray-border accent-hj-red cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">E-Mail</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Quelle</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Anmeldedatum</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hj-gray-border">
                {filtered.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(sub.id)}
                        onChange={() => toggleSelect(sub.id)}
                        className="w-4 h-4 rounded border-hj-gray-border accent-hj-red cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-hj-dark font-body">{sub.name}</td>
                    <td className="px-4 py-3 text-sm text-hj-gray font-body">{sub.email}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-xs bg-hj-gray-light rounded-full font-body text-hj-gray">{sub.source}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-hj-gray font-body">
                      {new Date(sub.subscribedAt).toLocaleDateString("de-CH", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleStatus(sub.id)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors cursor-pointer ${
                          sub.isActive
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        {sub.isActive ? "Aktiv" : "Inaktiv"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setDeleteTarget(sub)}
                        className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors cursor-pointer"
                        aria-label="Abonnent löschen"
                      >
                        <i className="ri-delete-bin-line text-red-500"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-hj-gray-light flex items-center justify-center mx-auto mb-3">
                <i className="ri-mail-line text-hj-gray text-xl"></i>
              </div>
              <p className="text-hj-gray font-body">Keine Abonnenten gefunden.</p>
            </div>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden">
          <MobileTableCards
            items={filtered}
            keyExtractor={(sub) => sub.id}
            emptyMessage="Keine Abonnenten gefunden."
            renderCard={(sub) => (
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                  sub.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {sub.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-hj-dark font-body">{sub.name}</p>
                  <p className="text-xs text-hj-gray font-body">{sub.email}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      sub.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {sub.isActive ? "Aktiv" : "Inaktiv"}
                    </span>
                    <span className="text-xs text-hj-muted font-body">{sub.source}</span>
                    <span className="text-xs text-hj-muted font-body">
                      {new Date(sub.subscribedAt).toLocaleDateString("de-CH")}
                    </span>
                  </div>
                </div>
              </div>
            )}
            onSwipeLeft={(sub) => setDeleteTarget(sub)}
            swipeLeftLabel="Löschen"
            renderActions={(sub) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleStatus(sub.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    sub.isActive
                      ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                  }`}
                >
                  {sub.isActive ? "Deaktivieren" : "Aktivieren"}
                </button>
              </div>
            )}
          />
        </div>

        {/* Results count */}
        <p className="text-xs text-hj-gray mt-3 font-body">
          {filtered.length} von {total} Abonnenten angezeigt
        </p>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        subscriber={deleteTarget}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Export Toast */}
      {exportToast && (
        <div className="fixed bottom-4 right-4 z-50 bg-hj-dark text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-in">
          <i className="ri-check-line text-emerald-400"></i>
          <span className="text-sm font-body">CSV erfolgreich exportiert</span>
        </div>
      )}
    </AdminLayout>
  );
}